import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, X } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import { solutionIndustries, site } from '@/data/content';

const details: Record<
  string,
  {
    challenge: { title: string; subtitle: string; points: { title: string; text: string }[] };
    cost: { title: string; points: { title: string; text: string }[] };
    solution: { title: string; subtitle: string; steps: { icon?: string; title: string; text: string }[] };
    dayInLife: { title: string; before: { title: string; text: string }[]; after: { title: string; text: string }[] };
    proof: { title: string; stats: { value: string; label: string }[] };
    why: { title: string; points: { title: string; text: string }[] };
    faqs: { q: string; a: string }[];
    heroImage?: string;
  }
> = {
  ecommerce: {
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/compressed-original.webp',
    challenge: {
      title: 'Shoppers abandon carts before you can answer',
      subtitle: 'Every unanswered question is a lost sale.',
      points: [
        { title: 'Questions go unanswered for hours', text: 'Shoppers ask about size, shipping, and stock across chat, email, and Instagram. By the time you reply, they have already bought elsewhere.' },
        { title: 'Returns get stuck in inboxes', text: 'Refund requests, order issues, and delivery complaints are spread across tools. Agents waste time switching apps instead of solving problems.' },
        { title: 'Reviews become missed opportunities', text: 'Happy customers are not asked to leave reviews at the right moment, while unhappy customers vent publicly.' },
      ],
    },
    cost: {
      title: 'What this costs your store',
      points: [
        { title: 'Lost revenue from abandoned carts', text: 'Up to 70% of online carts are abandoned when help is not instant.' },
        { title: 'Refund delays hurt repeat purchases', text: 'Slow returns turn one-time buyers into one-time buyers only.' },
        { title: 'Support costs grow with every channel', text: 'Every new channel adds another inbox to monitor and another tool to pay for.' },
      ],
    },
    solution: {
      title: 'How ZuriHub turns browsers into buyers',
      subtitle: 'One inbox, AI replies, and post-purchase workflows built for e-commerce.',
      steps: [
        { icon: 'Inbox', title: 'Capture every channel', text: 'WhatsApp, live chat, Instagram, email, and Facebook messages land in one shared inbox so no shopper is missed.' },
        { icon: 'Sparkles', title: 'AI replies from your catalog', text: 'Zyra drafts accurate answers using your product catalog, order policies, and shipping rules.' },
        { title: 'Automate returns and reviews', text: 'Route refund requests and send review invites at the perfect moment after delivery.' },
      ],
    },
    dayInLife: {
      title: 'A day in the life: e-commerce support team',
      before: [
        { title: 'Five apps open at once', text: 'WhatsApp, email, Instagram DMs, and live chat each have their own tab.' },
        { title: 'Copy order numbers by hand', text: 'Every refund means switching to the store admin, finding the order, and updating the customer separately.' },
        { title: 'Reviews are forgotten', text: 'Happy buyers are never asked to leave a review, while complaints go public.' },
      ],
      after: [
        { title: 'One inbox for every channel', text: 'All messages route into one queue with shopper history visible.' },
        { title: 'Order ID linked to ticket', text: 'The Shopify or WooCommerce order is right next to the conversation.' },
        { title: 'Reviews requested automatically', text: 'A WhatsApp message goes out the day after delivery to ask for a review.' },
      ],
    },
    proof: {
      title: 'What changes in 90 days',
      stats: [
        { value: '40%', label: 'faster replies' },
        { value: '25%', label: 'more recovered carts' },
        { value: '2x', label: 'repeat purchase rate' },
        { value: '60%', label: 'fewer refund disputes' },
      ],
    },
    why: {
      title: 'Why e-commerce teams choose ZuriHub',
      points: [
        { title: 'Unified shopper channels', text: 'One place for every message, no matter where the customer starts.' },
        { title: 'AI trained on your catalog', text: 'Zyra knows your products, policies, and stock so replies are fast and accurate.' },
        { title: 'Order tracking inside tickets', text: 'Order status, shipping, and refunds are visible next to each conversation.' },
        { title: 'Review automation', text: 'Turn happy shoppers into public reviews on autopilot.' },
      ],
    },
    faqs: [
      { q: 'Can ZuriHub connect to my Shopify or WooCommerce store?', a: 'Yes. You can embed the live chat widget, connect WhatsApp Business, and route order questions into one inbox.' },
      { q: 'How does AI help e-commerce support?', a: 'Zyra AI learns your product catalog and order policies to draft accurate replies to common shopper questions.' },
    ],
  },
  services: {
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/Service.webp',
    challenge: {
      title: 'Leads slip through the cracks',
      subtitle: 'Slow responses kill service-based businesses.',
      points: [
        { title: 'DMs are scattered', text: 'Leads come in through WhatsApp, Instagram, and email. If you do not respond in minutes, the next provider wins.' },
        { title: 'Quotes take days', text: 'Clients ask for estimates, but the back-and-forth across email and calls stretches into days.' },
        { title: 'Appointments are missed', text: 'Without reminders, clients forget bookings, and your revenue takes the hit.' },
      ],
    },
    cost: {
      title: 'What this costs your agency or practice',
      points: [
        { title: '30% of leads go cold', text: 'Speed is everything. A lead that waits more than an hour is usually a lost lead.' },
        { title: 'No-shows waste revenue', text: 'Every empty slot is money you cannot recover.' },
        { title: 'Clients churn after slow replies', text: 'Service businesses live on relationships. Slow replies signal that the client is not a priority.' },
      ],
    },
    solution: {
      title: 'How ZuriHub wins more clients',
      subtitle: 'Capture, qualify, quote, book, and follow up in one place.',
      steps: [
        { icon: 'Target', title: 'Collect leads from every channel', text: 'WhatsApp, Instagram, live chat, and email leads are tagged, scored, and routed instantly.' },
        { icon: 'FileText', title: 'Quote and book in one thread', text: 'Send estimates, get approvals, and confirm appointments without leaving the conversation.' },
        { icon: 'Calendar', title: 'Automate reminders and follow-ups', text: 'Reduce no-shows and keep clients coming back with scheduled WhatsApp and SMS messages.' },
      ],
    },
    dayInLife: {
      title: 'A day in the life: services team',
      before: [
        { title: 'Lead in WhatsApp, email, Instagram', text: 'Each channel has a different conversation and a different status.' },
        { title: 'Send quote from separate app', text: 'The client has to download, sign, and return a document before anything moves.' },
        { title: 'Forget to confirm booking', text: 'You assume the client remembers the time, but they do not.' },
      ],
      after: [
        { title: 'All leads in one queue', text: 'Every inquiry is tagged by service type, source, and urgency.' },
        { title: 'Quote sent and approved in thread', text: 'The client approves the estimate inside the same chat.' },
        { title: 'Auto reminders sent', text: 'Booking confirmations and reminders go out without manual work.' },
      ],
    },
    proof: {
      title: 'What changes in 90 days',
      stats: [
        { value: '50%', label: 'faster quote replies' },
        { value: '45%', label: 'fewer no-shows' },
        { value: '30%', label: 'more referrals' },
        { value: '2x', label: 'client retention' },
      ],
    },
    why: {
      title: 'Why service teams choose ZuriHub',
      points: [
        { title: 'One lead inbox', text: 'No matter where the inquiry starts, it lands in one place.' },
        { title: 'Quote and booking tracking', text: 'Every estimate, approval, and appointment is tied to the client record.' },
        { title: 'Client history visible', text: 'Your team sees every past quote, note, and conversation.' },
        { title: 'Auto follow-ups', text: 'Stay top of mind with clients without the manual effort.' },
      ],
    },
    faqs: [
      { q: 'Can clients book appointments through ZuriHub?', a: 'You can confirm bookings by message and send reminders. Calendar integrations are available on Enterprise.' },
      { q: 'Is client history preserved for teams?', a: 'Yes. Every quote, appointment, and note is stored on the client profile and searchable.' },
    ],
  },
  education: {
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/EDU.webp',
    challenge: {
      title: 'Admissions and support questions pile up',
      subtitle: 'Staff, students, and parents all need answers now.',
      points: [
        { title: 'The same questions are asked every day', text: 'Fees, deadlines, and program details take up hours that could be spent on real admissions work.' },
        { title: 'Departments use different inboxes', text: 'Admissions, IT, finance, and academic support each have their own email. Students do not know who to contact.' },
        { title: 'Urgent student issues get lost', text: 'A request for help can sit in the wrong inbox for days.' },
      ],
    },
    cost: {
      title: 'What this costs your institution',
      points: [
        { title: 'Admissions teams drown in email', text: 'Slow replies mean students choose another school first.' },
        { title: 'Parents feel ignored', text: 'Families expect the same speed from your school that they get from brands.' },
        { title: 'IT requests slow down learning', text: 'Technical problems that delay classes also delay progress.' },
      ],
    },
    solution: {
      title: 'How ZuriHub keeps everyone informed',
      subtitle: 'Self-service, smart routing, and shared inboxes for every department.',
      steps: [
        { icon: 'BookOpen', title: 'Deflect FAQs with a knowledge base', text: 'Students and parents find answers 24/7 before opening a ticket.' },
        { icon: 'Route', title: 'Route by department and urgency', text: 'Questions reach the right team instantly, with SLAs that keep things moving.' },
        { icon: 'Bell', title: 'Notify parents and staff in one place', text: 'Announcements and updates go out by WhatsApp, email, or SMS from one workspace.' },
      ],
    },
    dayInLife: {
      title: 'A day in the life: education support team',
      before: [
        { title: 'Email to the wrong department', text: 'The student copied five offices and no one knows who owns the reply.' },
        { title: 'Student waits for answer', text: 'A simple question about fees takes two days because it sat in an inbox.' },
        { title: 'Manual FAQ replies', text: 'Staff type the same answers over and over again.' },
      ],
      after: [
        { title: 'Knowledge base answers 60%', text: 'Common questions are resolved without a human ticket.' },
        { title: 'Auto-routing by topic', text: 'Admissions, IT, and finance each get the questions meant for them.' },
        { title: 'Parents updated by SMS', text: 'Fee deadlines and event updates reach families on their phones.' },
      ],
    },
    proof: {
      title: 'What changes in 90 days',
      stats: [
        { value: '60%', label: 'fewer repeated questions' },
        { value: '3x', label: 'faster admissions replies' },
        { value: '50%', label: 'less admin time' },
        { value: '35%', label: 'higher parent satisfaction' },
      ],
    },
    why: {
      title: 'Why education teams choose ZuriHub',
      points: [
        { title: 'Shared department inboxes', text: 'Admissions, IT, finance, and support work in one connected workspace.' },
        { title: 'Public help center', text: 'Students and parents self-serve around the clock.' },
        { title: 'AI FAQ replies', text: 'Common questions are answered automatically with accurate information.' },
        { title: 'Custom routing', text: 'Urgent issues reach the right person immediately.' },
      ],
    },
    faqs: [
      { q: 'Can each department have its own inbox?', a: 'Yes. Departments can be set up as teams with their own views, tags, and SLAs.' },
      { q: 'Is ZuriHub suitable for universities?', a: 'Yes. It scales from small schools to multi-campus institutions with role-based access.' },
    ],
  },
  health: {
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/HEALTH.webp',
    challenge: {
      title: 'Patient communication is slow and insecure',
      subtitle: 'Care teams and patients need fast, private, reliable messaging.',
      points: [
        { title: 'Appointments are missed', text: 'Patients forget visits because reminders are delayed, inconsistent, or use the wrong channel.' },
        { title: 'Patient data is scattered', text: 'Care team members message each other on personal apps with no audit trail.' },
        { title: 'Urgent triage is delayed', text: 'A critical patient message gets buried in a busy inbox.' },
      ],
    },
    cost: {
      title: 'What this costs your practice',
      points: [
        { title: 'Empty appointment slots', text: 'Every missed visit is lost revenue and worse care continuity.' },
        { title: 'Privacy risk', text: 'Informal messaging can violate patient data laws and damage trust.' },
        { title: 'Burned-out care teams', text: 'Unstructured communication makes every shift harder than it should be.' },
      ],
    },
    solution: {
      title: 'How ZuriHub keeps care moving',
      subtitle: 'Secure, compliant, patient-first messaging and scheduling.',
      steps: [
        { icon: 'CalendarHeart', title: 'Secure appointment reminders', text: 'Book, confirm, and remind patients via WhatsApp and SMS with clear audit trails.' },
        { icon: 'Users', title: 'Private care team messaging', text: 'Coordinate nurses, doctors, and admin in one secure channel.' },
        { icon: 'Shield', title: 'Triage by urgency', text: 'Route high-priority messages to the right caregiver immediately.' },
      ],
    },
    dayInLife: {
      title: 'A day in the life: health support team',
      before: [
        { title: 'Patient calls not returned', text: 'The front desk is busy and the message gets lost.' },
        { title: 'Care team messages on personal phones', text: 'Clinical questions are answered on WhatsApp with no record.' },
        { title: 'No audit trail', text: 'Compliance reviews have no message history to check.' },
      ],
      after: [
        { title: 'WhatsApp and SMS reminders', text: 'Patients get confirmed, reminded, and rescheduled automatically.' },
        { title: 'Secure care team channel', text: 'All clinical coordination happens in a controlled, auditable workspace.' },
        { title: 'Audit log for compliance', text: 'Every message, access, and action is logged for review.' },
      ],
    },
    proof: {
      title: 'What changes in 90 days',
      stats: [
        { value: '50%', label: 'fewer missed appointments' },
        { value: 'HIPAA', label: 'ready controls' },
        { value: '35%', label: 'faster triage' },
        { value: '24/7', label: 'patient messaging' },
      ],
    },
    why: {
      title: 'Why health teams choose ZuriHub',
      points: [
        { title: 'Secure messaging', text: 'Encrypted, role-based communication for care teams and patients.' },
        { title: 'Appointment workflows', text: 'Book, confirm, and remind patients in one flow.' },
        { title: 'Role-based access', text: 'Only the right people can see the right patient data.' },
        { title: 'Full audit trail', text: 'Compliance and quality reviews are always supported.' },
      ],
    },
    faqs: [
      { q: 'Is ZuriHub HIPAA-ready?', a: 'ZuriHub offers HIPAA-ready controls, role-based access, and encrypted messaging on Enterprise plans.' },
      { q: 'Can patients message on WhatsApp?', a: 'Yes. You can connect WhatsApp Business and route patient messages to the right caregiver.' },
    ],
  },
  finance: {
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/SA.webp',
    challenge: {
      title: 'Customer trust is hard to keep',
      subtitle: 'Regulated, high-stakes conversations demand speed and proof.',
      points: [
        { title: 'Account questions across channels', text: 'Customers ask about balances, transactions, and logins on chat, email, and voice. Replies are slow.' },
        { title: 'Disputes take too long', text: 'Investigations rely on scattered records and long email threads.' },
        { title: 'Fraud alerts get missed', text: 'A security message can sit unread while risk grows.' },
      ],
    },
    cost: {
      title: 'What this costs your financial service',
      points: [
        { title: 'Regulatory risk', text: 'Incomplete records and slow audit responses attract penalties.' },
        { title: 'Churn from slow support', text: 'When money is involved, customers expect immediate answers.' },
        { title: 'Fraud losses', text: 'Delayed escalation gives attackers more time.' },
      ],
    },
    solution: {
      title: 'How ZuriHub protects trust and speed',
      subtitle: 'One auditable inbox with AI, voice, and fraud routing.',
      steps: [
        { icon: 'Landmark', title: 'One auditable inbox', text: 'All account, card, and billing questions land in one tracked queue.' },
        { icon: 'Scale', title: 'Dispute workflow with traceability', text: 'Every step, note, and document lives in one thread with a full history.' },
        { icon: 'AlertTriangle', title: 'Instant fraud routing', text: 'High-priority security messages are escalated to the right team in seconds.' },
      ],
    },
    dayInLife: {
      title: 'A day in the life: finance support team',
      before: [
        { title: 'Customer emails one team, calls another', text: 'There is no single view of the customer across channels.' },
        { title: 'Dispute evidence in spreadsheets', text: 'Investigators waste time collecting documents from different places.' },
        { title: 'Fraud alert lost in inbox', text: 'A warning about suspicious activity sits unread in a busy queue.' },
      ],
      after: [
        { title: 'Single conversation history', text: 'Every channel, interaction, and document is tied to the customer.' },
        { title: 'Dispute tracked in one thread', text: 'The full lifecycle is visible and auditable from start to finish.' },
        { title: 'Fraud escalated in seconds', text: 'Alerts are automatically routed to the risk team with context.' },
      ],
    },
    proof: {
      title: 'What changes in 90 days',
      stats: [
        { value: '99.99%', label: 'uptime SLA' },
        { value: '60%', label: 'faster disputes' },
        { value: '40%', label: 'fewer escalations' },
        { value: 'Full', label: 'audit trail' },
      ],
    },
    why: {
      title: 'Why finance teams choose ZuriHub',
      points: [
        { title: 'Full audit trail', text: 'Every action and message is logged for compliance.' },
        { title: 'Role-based access', text: 'Control who sees sensitive customer data.' },
        { title: 'AI voice and secure chat', text: 'Talk and message within the same secure workspace.' },
        { title: '99.99% uptime', text: 'Reliable service for critical customer moments.' },
      ],
    },
    faqs: [
      { q: 'Is conversation history auditable?', a: 'Yes. Every message, ticket, and agent action is logged and searchable for compliance.' },
      { q: 'Can we separate customer support from internal teams?', a: 'Yes. Workspaces support owner, admin, and agent roles with fine-grained permissions.' },
    ],
  },
  saas: {
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/FEAT.avif',
    challenge: {
      title: 'Trials churn before they convert',
      subtitle: 'SaaS buyers expect instant answers and smooth onboarding.',
      points: [
        { title: 'Trial users have questions', text: 'A simple setup question can stop a trial from becoming a paid account.' },
        { title: 'Onboarding is manual', text: 'Customer success sends the same welcome messages and setup steps over and over.' },
        { title: 'Support cannot scale', text: 'As the user base grows, the team adds more agents, tools, and cost.' },
      ],
    },
    cost: {
      title: 'What this costs your SaaS business',
      points: [
        { title: '30% of trials churn silently', text: 'Users sign up, get stuck, and leave without ever asking for help.' },
        { title: 'Feature confusion', text: 'Users do not adopt advanced features because they do not understand them.' },
        { title: 'Support bottlenecks', text: 'Growth becomes a support cost problem instead of a revenue opportunity.' },
      ],
    },
    solution: {
      title: 'How ZuriHub converts and retains users',
      subtitle: 'Proactive outreach, self-serve docs, and AI-assisted support.',
      steps: [
        { icon: 'MessageSquare', title: 'Proactive trial outreach', text: 'Reach trial users on chat and WhatsApp before they give up.' },
        { icon: 'BookOpen', title: 'Self-serve knowledge base', text: 'Answer product questions while your team focuses on high-value work.' },
        { icon: 'Sparkles', title: 'AI-assisted support', text: 'Zyra drafts replies and summarizes threads so agents scale without more headcount.' },
      ],
    },
    dayInLife: {
      title: 'A day in the life: SaaS support team',
      before: [
        { title: 'Trial user waits for email reply', text: 'They submitted a form and wonder if anyone is there.' },
        { title: 'Support team overwhelmed', text: 'The same setup questions keep coming.' },
        { title: 'No onboarding tracking', text: 'No one knows which users are stuck or at risk of churning.' },
      ],
      after: [
        { title: 'Live chat pro-active prompts', text: 'A message appears when a trial user opens a key feature.' },
        { title: 'Knowledge base deflects 60%', text: 'Users help themselves before opening a ticket.' },
        { title: 'AI drafts replies', text: 'Agents review and send faster, even on complex questions.' },
      ],
    },
    proof: {
      title: 'What changes in 90 days',
      stats: [
        { value: '30%', label: 'lower churn' },
        { value: '25%', label: 'more trial conversions' },
        { value: '50%', label: 'faster onboarding' },
        { value: '10x', label: 'support scale' },
      ],
    },
    why: {
      title: 'Why SaaS teams choose ZuriHub',
      points: [
        { title: 'Proactive chat', text: 'Engage trials before they churn.' },
        { title: 'Product knowledge base', text: 'Users find answers without waiting.' },
        { title: 'AI reply suggestions', text: 'Agents handle more tickets with the same team size.' },
        { title: 'Scalable per-agent pricing', text: 'Grow support without breaking the budget.' },
      ],
    },
    faqs: [
      { q: 'Can ZuriHub replace Intercom for SaaS?', a: 'Yes. ZuriHub combines live chat, tickets, AI, and knowledge base at a lower per-agent cost.' },
      { q: 'Does it support in-app chat widgets?', a: 'Yes. The widget can be branded and embedded on any web app or site.' },
    ],
  },
  travel: {
    heroImage: 'https://media.getzurihub.com/Jayrah%20Assets/Travel.webp',
    challenge: {
      title: 'Travelers expect instant answers',
      subtitle: 'Hospitality is a 24/7 business with urgent, emotional requests.',
      points: [
        { title: 'Bookings change last minute', text: 'Flights, hotels, and transfers need fast updates across time zones.' },
        { title: 'Itinerary questions flood channels', text: 'Travelers ask the same questions on WhatsApp, email, and social, and expect a single answer.' },
        { title: 'Complaints hurt reviews', text: 'A single unresolved issue becomes a public one-star review.' },
      ],
    },
    cost: {
      title: 'What this costs your travel business',
      points: [
        { title: 'Lost rebooking revenue', text: 'Slow responses mean travelers book with competitors.' },
        { title: 'Negative reviews', text: 'One angry traveler can influence hundreds of potential customers.' },
        { title: '24/7 staffing cost', text: 'Round-the-clock coverage becomes expensive and hard to manage.' },
      ],
    },
    solution: {
      title: 'How ZuriHub keeps travelers happy',
      subtitle: 'Real-time booking support across every channel.',
      steps: [
        { icon: 'Plane', title: 'Real-time booking support', text: 'Manage reschedules, cancellations, and upgrades in the moment.' },
        { icon: 'Inbox', title: 'Multi-channel inbox', text: 'WhatsApp, email, live chat, and social land in one queue.' },
        { icon: 'Star', title: 'Review recovery', text: 'Resolve complaints quickly and ask satisfied travelers for reviews.' },
      ],
    },
    dayInLife: {
      title: 'A day in the life: travel support team',
      before: [
        { title: 'Traveler waits on hold', text: 'A flight is delayed and the phone line is busy.' },
        { title: 'Agent cannot see booking', text: 'The customer has to repeat the booking number on every channel.' },
        { title: 'Complaint goes viral', text: 'A frustrated tweet goes unanswered for hours.' },
      ],
      after: [
        { title: 'WhatsApp booking updates', text: 'Travelers get rebooking options in real time on their phones.' },
        { title: 'Booking context in ticket', text: 'The agent sees dates, destinations, and history instantly.' },
        { title: 'Reviews requested after resolution', text: 'Happy travelers are asked to leave a review at the right moment.' },
      ],
    },
    proof: {
      title: 'What changes in 90 days',
      stats: [
        { value: '45%', label: 'faster booking changes' },
        { value: '20%', label: 'better review scores' },
        { value: '30%', label: 'fewer escalations' },
        { value: '24/7', label: 'traveler support' },
      ],
    },
    why: {
      title: 'Why travel teams choose ZuriHub',
      points: [
        { title: 'Real-time chat', text: 'Answer urgent questions while travelers are still making decisions.' },
        { title: 'WhatsApp and email in one', text: 'One inbox for every channel travelers use.' },
        { title: 'Booking context in tickets', text: 'Agents see itinerary, dates, and notes without switching tools.' },
        { title: 'Review recovery', text: 'Turn negative moments into positive public reviews.' },
      ],
    },
    faqs: [
      { q: 'Can travelers reach us on WhatsApp?', a: 'Yes. WhatsApp Business is fully supported for booking updates and support.' },
      { q: 'Can agents see booking context?', a: 'Yes. Tickets can hold booking IDs, dates, and customer notes for fast resolutions.' },
    ],
  },
};

const fallback = {
  heroImage: undefined,
  challenge: {
    title: 'Customer conversations are scattered and slow',
    subtitle: 'Teams waste time switching between inboxes and tools.',
    points: [
      { title: 'Messages in too many places', text: 'Your customers write on WhatsApp, email, chat, and social, but your team has to check each one.' },
      { title: 'Replies are slow', text: 'Without a shared inbox, tickets sit unanswered while customers churn.' },
      { title: 'No shared context', text: 'Every agent starts from zero because history is spread across tools.' },
    ],
  },
  cost: {
    title: 'What this costs your team',
    points: [
      { title: 'Lost customers', text: 'Slow responses are one of the top reasons buyers choose a competitor.' },
      { title: 'Agent burnout', text: 'Switching tools makes every conversation take longer than it should.' },
      { title: 'Missed revenue', text: 'Leads and upsell opportunities are buried in unread messages.' },
    ],
  },
  solution: {
    title: 'How ZuriHub brings it all together',
    subtitle: 'One inbox, AI, and workflows that help your team win.',
    steps: [
      { icon: 'Inbox', title: 'Unify every channel', text: 'WhatsApp, email, live chat, Instagram, and more in one workspace.' },
      { icon: 'Sparkles', title: 'Let AI do the heavy lifting', text: 'Zyra drafts replies, summarizes threads, and deflects common questions.' },
      { icon: 'LineChart', title: 'Measure what matters', text: 'Track response time, CSAT, and resolution in real time.' },
    ],
  },
  dayInLife: {
    title: 'A day in the life: support team',
    before: [
      { title: 'Five tools open', text: 'Every channel is a separate tab or app.' },
      { title: 'No shared history', text: 'The customer has to repeat themselves.' },
      { title: 'Manual follow-up', text: 'Everything is tracked in spreadsheets or memory.' },
    ],
    after: [
      { title: 'One inbox', text: 'All conversations flow into a single queue.' },
      { title: 'Full customer context', text: 'Past messages, orders, and notes are visible instantly.' },
      { title: 'Automated follow-up', text: 'Tasks, reminders, and escalations run in the background.' },
    ],
  },
  proof: {
    title: 'What changes in 90 days',
    stats: [
      { value: '2x', label: 'faster responses' },
      { value: '30%', label: 'lower churn' },
      { value: '95%', label: 'CSAT score' },
      { value: '40%', label: 'less cost per ticket' },
    ],
  },
  why: {
    title: 'Why teams choose ZuriHub',
    points: [
      { title: 'One inbox for every channel', text: 'No more switching between apps.' },
      { title: 'AI that learns your business', text: 'Zyra gets smarter from your content and replies.' },
      { title: 'Works for your industry', text: 'Built-in workflows for e-commerce, services, health, finance, education, SaaS, and travel.' },
      { title: 'Priced per agent, not per channel', text: 'One flat price for everything.' },
    ],
  },
  faqs: [
    { q: 'Can I try ZuriHub for free?', a: 'Yes. New workspaces get a 14-day free trial with no credit card required.' },
    { q: 'How do I get started?', a: 'Create a workspace at business.zurichat.app, connect your channels, and invite your team.' },
  ],
};

export default function Industry() {
  const { slug } = useParams<{ slug: string }>();
  const industry = solutionIndustries.find((i) => i.slug === slug);

  if (!industry) {
    return (
      <section className="px-4 pt-32 pb-24 text-center">
        <h1 className="text-3xl font-extrabold text-slate-900">Solution not found</h1>
        <p className="mt-4 text-slate-600">Explore all ZuriHub solutions.</p>
        <Link to="/solutions" className="mt-6 inline-flex items-center gap-2 text-brand-600 font-bold">
          View solutions <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    );
  }

  const extra = details[industry.slug] || fallback;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: industry.metaTitle,
      provider: { '@type': 'Organization', name: site.name, url: site.baseUrl },
      areaServed: 'Global',
      url: `${site.baseUrl}/industry/${industry.slug}`,
      description: industry.metaDescription,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: extra.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <>
      <SEO
        title={industry.metaTitle}
        description={industry.metaDescription}
        path={`/industry/${industry.slug}`}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <Link to="/solutions" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-400 hover:text-white transition">
            <ArrowRight className="h-4 w-4 rotate-180" /> Solutions
          </Link>
          <div className="mt-6 grid items-center gap-10 lg:grid-cols-2">
            <div>
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-400 to-brand-700 shadow-lg">
                <Icon name={industry.icon} className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                {industry.headline}
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                {industry.summary}
              </p>
              <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
                >
                  Start free trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition hover:bg-slate-800"
                >
                  {industry.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
            {extra.heroImage ? (
              <div className="rounded-3xl border border-slate-700 bg-slate-900/50 p-2 backdrop-blur">
                <img
                  src={extra.heroImage}
                  alt={`${industry.name} solution illustration`}
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            ) : (
              <div className="rounded-3xl border border-slate-700 bg-slate-900/50 p-8 backdrop-blur">
                <h2 className="text-xl font-bold text-white">{extra.challenge.title}</h2>
                <p className="mt-2 text-slate-400">{extra.challenge.subtitle}</p>
                <div className="mt-6 space-y-6">
                  {extra.challenge.points.map((p) => (
                    <div key={p.title} className="flex gap-4">
                      <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-500/20 text-brand-400">
                        <Check className="h-3.5 w-3.5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{p.title}</h3>
                        <p className="mt-1 text-sm text-slate-400">{p.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="cost-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="cost-heading" className="section-title">{extra.cost.title}</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {extra.cost.points.map((p) => (
              <div
                key={p.title}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm text-center"
              >
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                  <X className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-extrabold text-slate-900">{p.title}</h3>
                <p className="mt-3 text-slate-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="solution-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="solution-heading" className="section-title">{extra.solution.title}</h2>
            <p className="section-subtitle mx-auto">{extra.solution.subtitle}</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {extra.solution.steps.map((s, i) => (
              <div
                key={s.title}
                className="rounded-3xl border border-slate-100 bg-slate-50 p-8 text-center transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                  <span className="text-2xl font-extrabold text-brand-700">{i + 1}</span>
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{s.title}</h3>
                <p className="mt-3 text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="day-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="day-heading" className="section-title text-white">{extra.dayInLife.title}</h2>
            <p className="section-subtitle mx-auto text-slate-300">Same team, different tools. Different results.</p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="text-xl font-bold text-slate-300">Without ZuriHub</h3>
              <div className="mt-6 space-y-5">
                {extra.dayInLife.before.map((b) => (
                  <div key={b.title} className="flex gap-4">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/20 text-red-400">
                      <X className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{b.title}</h4>
                      <p className="mt-1 text-sm text-slate-400">{b.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="text-xl font-bold text-white">With ZuriHub</h3>
              <div className="mt-6 space-y-5">
                {extra.dayInLife.after.map((a) => (
                  <div key={a.title} className="flex gap-4">
                    <div className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-400">
                      <Check className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{a.title}</h4>
                      <p className="mt-1 text-sm text-slate-400">{a.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="proof-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="proof-heading" className="section-title">{extra.proof.title}</h2>
            <p className="section-subtitle mx-auto">
              Results {industry.name.toLowerCase()} teams typically see after switching to ZuriHub.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {extra.proof.stats.map((s) => (
              <div
                key={s.label}
                className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm"
              >
                <p className="text-4xl font-extrabold text-brand-700">{s.value}</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="why-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="why-heading" className="section-title">{extra.why.title}</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {extra.why.points.map((p) => (
              <div
                key={p.title}
                className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <Check className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-slate-600">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-white text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-4">
            {extra.faqs.map((f) => (
              <div
                key={f.q}
                className="rounded-2xl border border-slate-700 bg-slate-800/50 p-6"
              >
                <h3 className="font-bold text-white">{f.q}</h3>
                <p className="mt-2 text-slate-300">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Ready to fix {industry.name.toLowerCase()} customer experience?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Start a 14-day free trial and launch the workspace built for {industry.name.toLowerCase()} teams.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
